/**
 * AISessionBadge
 * Feature: 071-ai-critique-sessions
 * Task: T100
 *
 * Displays a visual indicator for AI critique sessions.
 * Shows a purple badge with sparkles icon for sessions with intent='ai_only'.
 */

interface AISessionBadgeProps {
  /** Whether this session is an AI session (intent='ai_only') */
  isAiSession?: boolean;
  /** Optional CSS classes */
  className?: string;
}

/**
 * Badge showing AI session indicator
 *
 * Only renders when isAiSession is true. Shows a distinctive
 * purple badge with sparkles icon to distinguish AI sessions
 * from regular critique sessions.
 *
 * @example
 * ```tsx
 * <AISessionBadge isAiSession={session.isAiSession} />
 * ```
 */
export function AISessionBadge({
  isAiSession,
  className = '',
}: AISessionBadgeProps) {
  // Don't render anything if not an AI session
  if (!isAiSession) {
    return null;
  }

  return (
    <span
      className={`
        inline-flex items-center gap-1 px-2 py-0.5 rounded-full
        bg-purple-500/10 text-purple-600 dark:text-purple-400
        border border-purple-500/20
        text-xs font-medium
        ${className}
      `}
      title="AI Discussion - Feedback provided by AI personas"
    >
      {/* Sparkles icon */}
      <svg
        className="w-3 h-3"
        fill="currentColor"
        viewBox="0 0 24 24"
      >
        <path d="M12 0L14.59 8.41L23 11L14.59 13.59L12 22L9.41 13.59L1 11L9.41 8.41L12 0Z" />
        <path d="M5 2L5.82 4.18L8 5L5.82 5.82L5 8L4.18 5.82L2 5L4.18 4.18L5 2Z" opacity="0.5" />
        <path d="M19 16L19.82 18.18L22 19L19.82 19.82L19 22L18.18 19.82L16 19L18.18 18.18L19 16Z" opacity="0.5" />
      </svg>
      <span>AI</span>
    </span>
  );
}

export default AISessionBadge;
